
// V4.1: Modüler import'lar
import React, { useState, useEffect, Suspense } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { collection, addDoc, deleteDoc, doc, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged, createUserWithEmailAndPassword } from 'firebase/auth';

// V4.1: Import utilities ve constants
import { db, auth } from './utils/firebase';
// V5.5: verifyPassword ve hashPassword kaldırıldı - Sadece Firebase Auth kullanılıyor
import { createEmailFromName } from './utils/formatters';
import { VARSAYILAN_ALANLAR, VARSAYILAN_SUBELER } from './constants';
import { styles } from './constants/styles';

// V4.2.1: Toast notification
import { useToast } from './components/Toast';
import { alertToToast } from './utils/toast';
import { log, error, warn } from './utils/logger';

// V4.2.3: Loading components
import { LoadingSpinner } from './components/Loading';

// V4.4.3: Code splitting - Büyük component'leri lazy load
import YoneticiAnasayfa from './components/YoneticiAnasayfa';
const VeriGirisi = React.lazy(() => import('./components/VeriGirisi'));
const YeniKumbara = React.lazy(() => import('./components/YeniKumbara'));
const MerkezBaskanAnasayfa = React.lazy(() => import('./components/MerkezBaskanAnasayfa'));
const IlHayratAnasayfa = React.lazy(() => import('./components/IlHayratAnasayfa'));
const SubeHayratAnasayfa = React.lazy(() => import('./components/SubeHayratAnasayfa'));
const SubeYonetimi = React.lazy(() => import('./components/SubeYonetimi'));
const KumbaraListesi = React.lazy(() => import('./components/KumbaraListesi'));
const TeslimKumbaralar = React.lazy(() => import('./components/TeslimKumbaralar'));
const AcilanKumbaralar = React.lazy(() => import('./components/AcilanKumbaralar'));
const SahaAnasayfa = React.lazy(() => import('./components/SahaAnasayfa'));
const Ayarlar = React.lazy(() => import('./components/Ayarlar'));
const Raporlar = React.lazy(() => import('./components/Raporlar'));
const MaliIslemler = React.lazy(() => import('./components/MaliIslemler'));

const KULLANICILAR = [
  { id: 1, ad: 'Ahmet Yılmaz', rol: 'Merkez Başkanı', sifre: '1234', subeId: 'merkez' },
  { id: 2, ad: 'Mehmet Kaya', rol: 'Kumbara Koordinatörü', sifre: '1234', subeId: 'merkez' },
  { id: 3, ad: 'Hasan Öztürk', rol: 'İl Merkez Hayrat Koordinatörü', sifre: '1234', subeId: 'merkez' },
  { id: 4, ad: 'Ayşe Demir', rol: 'Şube Hayrat Sorumlusu', sifre: '1234', subeId: 'irsad' },
  { id: 5, ad: 'Fatma Şahin', rol: 'Şube Hayrat Sorumlusu', sifre: '1234', subeId: 'isar' },
  { id: 6, ad: 'Ali Çelik', rol: 'Saha Görevlisi', sifre: '1234', subeId: 'merkez', alanlar: ['İlkadım', 'Canik'] },
  { id: 7, ad: 'Veli Yıldız', rol: 'Saha Görevlisi', sifre: '1234', subeId: 'irsad', alanlar: ['Tekkeköy'] },
  { id: 8, ad: 'Zeynep Arslan', rol: 'Saha Görevlisi', sifre: '1234', subeId: 'isar', alanlar: ['Atakum'] },
  { id: 9, ad: 'Elif Korkmaz', rol: 'Saha Görevlisi', sifre: '1234', subeId: null, alanlar: ['Merkez'] },
  { id: 10, ad: 'Mustafa Aydın', rol: 'Saha Görevlisi', sifre: '1234', subeId: 'merkez', alanlar: ['Canik'] }
];

// V4.1: Styles artık constants/styles.js'de import ediliyor

function App() {
  // V4.2.1: Toast hook
  const toast = useToast();
  
  // V5.8: PWA Service Worker kaydı ve Install Prompt
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  
  // V5.10.8: Service Worker TAMAMEN devre dışı - Cache sorunları nedeniyle
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      // Tüm Service Worker'ları unregister et (cache temizleme için)
      window.addEventListener('load', () => {
        navigator.serviceWorker.getRegistrations().then((registrations) => {
          registrations.forEach((registration) => {
            registration.unregister().then(() => {
              log('🗑️ Service Worker unregister edildi (tamamen devre dışı):', registration.scope);
            });
          });
        });
        
        // Service Worker'ı kaydetme - TAMAMEN DEVRE DIŞI
        warn('⚠️ Service Worker devre dışı bırakıldı (V5.10.8 - Cache sorunları nedeniyle)');
      });
    }
    
    // PWA install prompt yakalama
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      log('✅ PWA install prompt yakalandı');
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    // Zaten yüklü mü kontrol et
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsInstalled(true);
    }
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);
  
  const handleInstallClick = async () => {
    // Chrome/Edge desktop veya Android Chrome - beforeinstallprompt event'i var
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      log('PWA install sonucu:', outcome);
      
      if (outcome === 'accepted') {
        alertToToast(toast, '✅ Uygulama ana ekrana eklendi!');
        setIsInstalled(true);
      }
      
      setDeferredPrompt(null);
      return;
    }
    
    // Safari tespiti: userAgent içinde 'Safari' var ama 'Chrome' yoksa Safari'dir
    const userAgent = navigator.userAgent;
    const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);

    if (isSafari) {
      const message = '📱 Ana Ekrana Ekleme Talimatları:\n\n' +
        '1. Safari\'nin alt kısmındaki "Paylaş" butonuna (⬆️) tıklayın\n' +
        '2. "Ana Ekrana Ekle" seçeneğini seçin\n' +
        '3. "Ekle" butonuna tıklayın';

      alertToToast(toast, message, 0); // 0 = otomatik kapanmaz, kullanıcı X'e basana kadar açık kalır
    }
  };
  
  const [aktifKullanici, setAktifKullanici] = useState(() => {
    try {
      const kayitliKullanici = localStorage.getItem('kumbaraAktifKullanici');
      return kayitliKullanici ? JSON.parse(kayitliKullanici) : null;
    } catch {
      return null;
    }
  }); // Sayfa yenilendiğinde localStorage'dan otomatik giriş
  const [aktifSayfa, setAktifSayfa] = useState('anasayfa');
  const [mobilMenuAcik, setMobilMenuAcik] = useState(false);
  const [kumbaralar, setKumbaralar] = useState([]);
  const [teslimEdilenler, setTeslimEdilenler] = useState([]);
  const [acilmisKumbaralar, setAcilmisKumbaralar] = useState([]);
  const [dagitimdaBekleyen] = useState(50);
  const [sahaKonum, setSahaKonum] = useState({ lat: 41.2867, lon: 36.3300 }); // Samsun koordinatları (varsayılan)
  const [kumbaraYuklendi, setKumbaraYuklendi] = useState(false);
  const [subeYuklendi, setSubeYuklendi] = useState(false);
  const [acilmisYuklendi, setAcilmisYuklendi] = useState(false);
  const yukleniyor = !kumbaraYuklendi || !subeYuklendi || !acilmisYuklendi;
  const [kullanicilar, setKullanicilar] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [alanlar, setAlanlar] = useState([]);
  const [subeler, setSubeler] = useState([]);

  // GPS: Kullanıcının anlık konumunu sürekli takip et
  useEffect(() => {
    if (!navigator.geolocation) return;
    const watchId = navigator.geolocation.watchPosition(
      (pos) => setSahaKonum({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      () => {}, // Hata durumunda varsayılan koordinat kalır
      { enableHighAccuracy: true, maximumAge: 30000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  // V4.7: Auth state değişikliklerini dinle (sadece logout için)
  // NOT: onAuthStateChanged tüm tab'larda tetiklenir, bu yüzden dikkatli olmalıyız
  // Çoklu tab sorununu önlemek için: Sadece bu tab'da manuel çıkış yapıldığında state'i temizle
  // V5.7: Çoklu cihaz kontrolü tekrar aktif edildi (bildirim sorunları düzeltildi)
  const [mevcutSessionId, setMevcutSessionId] = useState(null);
  const [merkezMaliSube, setMerkezMaliSube] = useState(null);
  const manuelCikisYapildi = React.useRef(false); // Bu tab'da manuel çıkış yapıldı mı?
  
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      // V4.7: Sadece bu tab'da manuel çıkış yapıldıysa state'i temizle
      // Diğer tab'lardaki logout'ları ignore et
      if (!user && aktifKullanici) {
        if (manuelCikisYapildi.current) {
          // Bu tab'da manuel çıkış yapıldı
          log('⚠️ Auth state değişti: Bu tab\'da çıkış yapıldı');
          manuelCikisYapildi.current = false; // Reset
          setAktifKullanici(null);
          setMevcutSessionId(null);
        } else {
          // Başka tab'dan logout yapıldı - IGNORE ET (Firestore listener'lar çalışmaya devam etsin)
          log('⚠️ Auth state değişti ama başka tab\'dan, ignore ediliyor (listener\'lar çalışmaya devam edecek)');
          // State'i temizleme - listener'lar çalışmaya devam etsin
        }
      } else if (user && !aktifKullanici) {
        // Yeni giriş yapıldı - ref'i reset et ve token refresh yap
        manuelCikisYapildi.current = false;
        // V4.7: Giriş yapıldığında token refresh yap (listener'ların fresh token ile çalışması için)
        (async () => {
          try {
            await user.getIdToken(true); // Force refresh
            log('✅ Yeni giriş sonrası token refresh tamamlandı');
          } catch (error) {
            warn('⚠️ Yeni giriş sonrası token refresh hatası:', error);
          }
        })();
      }
    });
    
    return () => unsubscribeAuth();
  }, [aktifKullanici, mevcutSessionId]);

  // V4.8: Kullanıcıları yükle (login için gerekli - her zaman çalışmalı)
  // V5.10.19: 10sn içinde veri gelmezse listener'ı yeniden başlat (maks 3 deneme)
  useEffect(() => {
    let unsubscribeKullanicilar = () => {};
    let zamanAsimiId = null;
    let denemeSayisi = 0;
    const MAKS_DENEME = 3;
    const ZAMAN_ASIMI_MS = 10000;
    let durduruldu = false;

    const listenerBaslat = () => {
      if (durduruldu) return;

      zamanAsimiId = setTimeout(() => {
        if (denemeSayisi < MAKS_DENEME) {
          denemeSayisi++;
          warn(`⚠️ Kullanıcılar listener ${ZAMAN_ASIMI_MS / 1000}sn içinde veri vermedi, yeniden başlatılıyor (deneme ${denemeSayisi}/${MAKS_DENEME})`);
          unsubscribeKullanicilar();
          listenerBaslat();
        } else {
          error(`❌ Kullanıcılar listener ${MAKS_DENEME} denemede de veri alamadı`);
        }
      }, ZAMAN_ASIMI_MS);

      unsubscribeKullanicilar = onSnapshot(collection(db, 'kullanicilar'), (snapshot) => {
        clearTimeout(zamanAsimiId);
        denemeSayisi = 0;

        const kullaniciData = snapshot.docs.map(doc => ({
          firestoreId: doc.id,
          ...doc.data()
        }));

        log('📋 Kullanıcılar yüklendi:', kullaniciData.length, 'kullanıcı');
        log('📋 Kullanıcı listesi:', kullaniciData.map(k => ({ ad: k.ad, id: k.id, firestoreId: k.firestoreId, rol: k.rol })));

        if (kullaniciData.length > 0) {
          setKullanicilar(kullaniciData);
        } else {
          // İlk kez kullanıyorsa örnek kullanıcılar ekle
          log('⚠️ Kullanıcı yok, örnek kullanıcılar ekleniyor...');
          KULLANICILAR.forEach(k => addDoc(collection(db, 'kullanicilar'), k).catch(() => {}));
        }
      }, (error) => {
        // V4.3: Permission hatası olsa bile state'i güncelle (boş array)
        clearTimeout(zamanAsimiId);
        log('⚠️ Kullanıcılar listener hatası:', error.code, error.message);
        setKullanicilar([]);
        if (error.code !== 'permission-denied') {
          error('❌ Kullanıcılar yükleme hatası:', error);
        }
      });
    };

    listenerBaslat();

    return () => {
      durduruldu = true;
      clearTimeout(zamanAsimiId);
      unsubscribeKullanicilar();
    };
  }, []); // V4.8: Her zaman çalışmalı (giriş ekranında gerekli)

  // V4.8: Firebase'den verileri yükle - aktifKullanici değiştiğinde yeniden başlat
  // Bu sayede her giriş yapıldığında fresh token ile listener'lar başlatılır
  useEffect(() => {
    // V4.8: Giriş yapılmadıysa listener'ları başlatma (permission hatası almasın)
    if (!aktifKullanici) {
      log('⏸️ Aktif kullanıcı yok, listener\'lar başlatılmıyor');
      return;
    }
    
    // V5.10.22: Listener başlatma log'u kaldırıldı
    
    // V4.8: Önce token refresh yap (listener'lar fresh token ile başlasın)
    (async () => {
      try {
        const { auth } = await import('./utils/firebase');
        const user = auth.currentUser;
        if (user) {
          await user.getIdToken(true); // Force refresh
          log('✅ Listener başlatmadan önce token refresh tamamlandı');
        }
      } catch (error) {
        warn('⚠️ Listener başlatmadan önce token refresh hatası:', error);
      }
    })();
    
    // V4.8: Şubeler listener + detaylı debug
    const unsubscribeSubeler = onSnapshot(collection(db, 'subeler'), (snapshot) => {
      const subeData = snapshot.docs.map(doc => ({
        firestoreId: doc.id,
        ...doc.data()
      }));
      
      // V4.6: Detaylı log
      log('🏢 Şubeler listener çalıştı:', subeData.length, 'şube');
      log('🏢 Şube ID\'leri:', subeData.map(s => s.id));
      
      if (subeData.length > 0) {
        setSubeler(subeData);
      } else {
        // İlk kez kullanıyorsa varsayılan şubeler ekle
        VARSAYILAN_SUBELER.forEach(sube =>
          addDoc(collection(db, 'subeler'), sube).catch(() => {})
        );
      }
      setSubeYuklendi(true);
    }, (error) => {
      // V4.6: Permission hatası olsa bile state'i güncelle (boş array) + detaylı log
      error('❌ Şubeler listener hatası:', error.code, error.message);
      error('❌ Hata stack:', error.stack);
      setSubeler([]);
      setSubeYuklendi(true);
      if (error.code !== 'permission-denied') {
        error('❌ Şubeler yükleme hatası (permission dışı):', error);
      } else {
        warn('⚠️ Şubeler okuma izni yok (permission-denied)');
      }
    });

    // Alanları yükle
    const unsubscribeAlanlar = onSnapshot(collection(db, 'alanlar'), async (snapshot) => {
      const alanData = snapshot.docs.map(doc => ({
        firestoreId: doc.id,
        ...doc.data()
      }));
      
      if (alanData.length > 0) {
        setAlanlar(alanData);
        
        // V5.9: Buhara Gönüllüsü için alanları otomatik güncelle
        if (aktifKullanici && aktifKullanici.ad === 'Buhara Gönüllüsü') {
          const mevcutAlanAdlari = alanData.map(a => a.ad || a.id).filter(Boolean);
          
          // Firestore'daki kullanıcıyı bul ve alanlarını güncelle
          try {
            const { getDocs, query, where, updateDoc, doc } = await import('firebase/firestore');
            const kullaniciSnapshot = await getDocs(
              query(collection(db, 'kullanicilar'), where('ad', '==', 'Buhara Gönüllüsü'))
            );
            
            if (!kullaniciSnapshot.empty) {
              const kullaniciDoc = kullaniciSnapshot.docs[0];
              const kullaniciData = kullaniciDoc.data();
              const mevcutAlanlar = kullaniciData.alanlar || [];
              
              // Alanlar farklıysa güncelle
              const alanlarEsit = mevcutAlanAdlari.length === mevcutAlanlar.length &&
                mevcutAlanAdlari.every(alan => mevcutAlanlar.includes(alan));
              
              if (!alanlarEsit) {
                await updateDoc(doc(db, 'kullanicilar', kullaniciDoc.id), {
                  alanlar: mevcutAlanAdlari
                });
                log('✅ Buhara Gönüllüsü alanları güncellendi:', mevcutAlanAdlari);
                
                // aktifKullanici state'ini de güncelle
                setAktifKullanici(prev => ({
                  ...prev,
                  alanlar: mevcutAlanAdlari
                }));
              }
            }
          } catch (error) {
            warn('⚠️ Buhara Gönüllüsü alanları güncellenemedi:', error);
          }
        }
      } else {
        // İlk kez kullanıyorsa varsayılan alanlar ekle
        VARSAYILAN_ALANLAR.forEach((alan, index) => 
          addDoc(collection(db, 'alanlar'), { id: index + 1, ad: alan }).catch(() => {})
        );
      }
    }, (error) => {
      // İzin hatası normal (giriş yapmadan önce)
      if (error.code !== 'permission-denied') {
        error('Alanlar yükleme hatası:', error);
      }
    });


    // V4.6: Listener'ı her zaman aktif tut + detaylı debug
    const unsubscribe = onSnapshot(collection(db, 'kumbaralar'), (snapshot) => {
      const kumbaraData = snapshot.docs.map(doc => ({
        firestoreId: doc.id,
        ...doc.data(),
        sonZiyaret: doc.data().sonZiyaret?.toDate() || new Date()
      }));
      
      // V4.6: Her zaman state'i güncelle + detaylı log
      log('📦 Kumbaralar listener çalıştı:', kumbaraData.length, 'adet');
      log('📦 Kumbara ID\'leri:', kumbaraData.map(k => k.id).slice(0, 5));
      setKumbaralar(kumbaraData);
      setKumbaraYuklendi(true);
    }, (error) => {
      // V4.6: Permission hatası olsa bile state'i güncelle (boş array) + detaylı log
      error('❌ Kumbaralar listener hatası:', error.code, error.message);
      error('❌ Hata stack:', error.stack);
      setKumbaralar([]);
      setKumbaraYuklendi(true);
      if (error.code !== 'permission-denied') {
        error('❌ Kumbaralar yükleme hatası (permission dışı):', error);
      } else {
        warn('⚠️ Kumbaralar okuma izni yok (permission-denied)');
      }
    });

    // V4.6: Açılmış kumbaralar listener + detaylı debug
    const unsubscribeAcilmis = onSnapshot(collection(db, 'acilmisKumbaralar'), (snapshot) => {
      // V5.10.6: Duplicate önlemek için - Her document'i sadece bir kez işle
      const acilmisData = snapshot.docs.map(doc => {
        const data = doc.data();
        let acilisTarihi = new Date();
        if (data.acilisTarihi) {
          if (data.acilisTarihi.toDate) {
            acilisTarihi = data.acilisTarihi.toDate();
          } else if (data.acilisTarihi instanceof Date) {
            acilisTarihi = data.acilisTarihi;
          } else {
            acilisTarihi = new Date(data.acilisTarihi);
          }
        }
        return {
          firestoreId: doc.id, // V5.10.6: Unique key - duplicate önlemek için
          ...data,
          acilisTarihi: acilisTarihi
        };
      });
      
      // V5.10.6: Duplicate kontrolü - Aynı firestoreId'ye sahip kayıtları filtrele
      const uniqueAcilmisData = acilmisData.filter((item, index, self) => 
        index === self.findIndex(t => t.firestoreId === item.firestoreId)
      );
      
      // V4.6: Her zaman state'i güncelle + detaylı log
      log('✅ Açılmış kumbaralar listener çalıştı:', uniqueAcilmisData.length, 'kumbara (duplicate kontrolü yapıldı)');
      log('✅ Açılmış kumbara ID\'leri:', uniqueAcilmisData.map(k => k.id).slice(0, 5));
      log('✅ Açılmış kumbara Firestore ID\'leri:', uniqueAcilmisData.map(k => k.firestoreId).slice(0, 5));
      setAcilmisKumbaralar(uniqueAcilmisData);
      setAcilmisYuklendi(true);
    }, (error) => {
      // V4.6: Permission hatası olsa bile state'i güncelle (boş array) + detaylı log
      error('❌ Açılmış kumbaralar listener hatası:', error.code, error.message);
      error('❌ Hata stack:', error.stack);
      setAcilmisKumbaralar([]);
      setAcilmisYuklendi(true);
      if (error.code !== 'permission-denied') {
        error('❌ Açılmış kumbaralar yükleme hatası (permission dışı):', error);
      } else {
        warn('⚠️ Açılmış kumbaralar okuma izni yok (permission-denied)');
      }
    });

    // V4.8: Teslim edilenler listener
    const unsubscribeTeslim = onSnapshot(collection(db, 'teslimEdilenler'), (snapshot) => {
      const teslimData = snapshot.docs.map(doc => ({
        firestoreId: doc.id,
        ...doc.data(),
        teslimTarihi: doc.data().teslimTarihi?.toDate() || new Date()
      }));
      setTeslimEdilenler(teslimData);
    }, (error) => {
      // İzin hatası normal (giriş yapmadan önce)
      if (error.code !== 'permission-denied') {
        error('Teslim edilenler yükleme hatası:', error);
      }
    });

    return () => {
      log('🛑 Listener\'lar durduruluyor (aktifKullanici değişti veya component unmount)');
      unsubscribeSubeler();
      unsubscribeAlanlar();
      unsubscribe();
      unsubscribeAcilmis();
      unsubscribeTeslim();
    };
  }, [aktifKullanici]); // V4.8: aktifKullanici değiştiğinde listener'ları yeniden başlat

  // V4.5: Periyodik token refresh - Token'ın her zaman fresh olması için 30 dakikada bir refresh et
  useEffect(() => {
    if (!aktifKullanici) return;
    
    const refreshInterval = setInterval(async () => {
      try {
        const { auth } = await import('./utils/firebase');
        const user = auth.currentUser;
        if (user) {
          await user.getIdToken(true); // Force refresh - custom claims yüklensin
          log('✅ Token otomatik refresh edildi (30 dakika)');
        }
      } catch (error) {
        warn('⚠️ Token otomatik refresh hatası:', error);
      }
    }, 30 * 60 * 1000); // 30 dakika (token 1 saat expire oluyor, yarısında refresh)
    
    return () => clearInterval(refreshInterval);
  }, [aktifKullanici]);

  const [girisForm, setGirisForm] = useState({ kullaniciId: '', sifre: '' });
  const [sifreGoster, setSifreGoster] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState({});

  // V5.7: Çoklu cihaz kontrolü - Tekrar aktif edildi (bildirim sorunları düzeltildi)
  // Güvenli kontrol - session gerçekten oluşana kadar hiçbir şey yapma
  // V5.10.21: Session listener TAMAMEN KALDIRILDI (console spam önleme)
  // useEffect(() => { ... }, [aktifKullanici, mevcutSessionId, toast]); // KALDIRILDI

  const girisYap = async () => {
    if (!girisForm.kullaniciId || !girisForm.sifre) {
      alertToToast(toast, '❌ Lütfen kullanıcı seçin ve şifre girin!');
      return;
    }
    
    // Rate Limiting - 5 yanlış deneme kontrolü
    const now = Date.now();
    const userAttempts = loginAttempts[girisForm.kullaniciId] || { count: 0, blockedUntil: 0 };
    
    if (userAttempts.blockedUntil > now) {
      const remainingMinutes = Math.ceil((userAttempts.blockedUntil - now) / 60000);
      alertToToast(toast, `🚫 Çok fazla yanlış deneme! ${remainingMinutes} dakika sonra tekrar deneyin.`);
      return;
    }
    
    // V5.9: Hem id hem firestoreId ile arama yap (yeni kullanıcılar için)
    const kullanici = kullanicilar.find(k => 
      k.id === parseInt(girisForm.kullaniciId) || 
      k.id?.toString() === girisForm.kullaniciId || 
      k.firestoreId === girisForm.kullaniciId
    );
    if (!kullanici) {
      alertToToast(toast, '❌ Kullanıcı bulunamadı!');
      return;
    }
    
    // V5.5: Firestore şifre kontrolü kaldırıldı - Sadece Firebase Auth kullanılıyor

    // V5.5: Sadece Firebase Auth ile giriş (Firestore fallback kaldırıldı)
    try {
      const email = createEmailFromName(kullanici.ad);
      log('🔐 Firebase Auth giriş denemesi:', {
        kullaniciAd: kullanici.ad,
        email: email,
        sifreUzunluk: girisForm.sifre.length
      });
      
      // Firebase Auth ile giriş yap
      try {
        await signInWithEmailAndPassword(auth, email, girisForm.sifre);
        log('✅ Firebase Auth giriş başarılı');
      } catch (authError) {
        log('⚠️ Firebase Auth giriş hatası:', authError.code, authError.message);
        
        // Kullanıcı yoksa oluştur
        if (authError.code === 'auth/user-not-found') {
          log('📝 Kullanıcı bulunamadı, oluşturuluyor...');
          try {
            await createUserWithEmailAndPassword(auth, email, girisForm.sifre);
            log('✅ Kullanıcı oluşturuldu:', email);
          } catch (createError) {
            error('❌ Kullanıcı oluşturma hatası:', createError.code, createError.message);
            // Rate limiting - yanlış deneme sayısını artır
            const newCount = userAttempts.count + 1;
            const newAttempts = { ...loginAttempts };
            
            if (newCount >= 5) {
              newAttempts[girisForm.kullaniciId] = {
                count: 0,
                blockedUntil: now + (5 * 60 * 1000)
              };
              alertToToast(toast, '🚫 5 kez yanlış şifre girdiniz! 5 dakika bekleyin.');
            } else {
              newAttempts[girisForm.kullaniciId] = {
                count: newCount,
                blockedUntil: 0
              };
              alertToToast(toast, `❌ Giriş başarısız! (${newCount}/5 deneme)`);
            }
            setLoginAttempts(newAttempts);
            return;
          }
        } else if (authError.code === 'auth/invalid-credential' || authError.code === 'auth/wrong-password') {
          // V5.9: Şifre yanlış - kullanıcı oluşturma denemesi kaldırıldı (sabit kullanıcı listesi)
          log('⚠️ Firebase Auth şifre hatası - şifre yanlış');
          const newCount = userAttempts.count + 1;
          const newAttempts = { ...loginAttempts };
          
          if (newCount >= 5) {
            newAttempts[girisForm.kullaniciId] = {
              count: 0,
              blockedUntil: now + (5 * 60 * 1000)
            };
            alertToToast(toast, '🚫 5 kez yanlış şifre girdiniz! 5 dakika bekleyin.');
          } else {
            newAttempts[girisForm.kullaniciId] = {
              count: newCount,
              blockedUntil: 0
            };
            alertToToast(toast, `❌ Şifre yanlış! (${newCount}/5 deneme)`);
          }
          setLoginAttempts(newAttempts);
          return;
        } else {
          // Diğer hatalar
          error('❌ Firebase Auth hatası:', authError.code, authError.message);
          alertToToast(toast, `❌ Giriş hatası: ${authError.message}`);
          return;
        }
      }
      
      // Başarılı giriş - denemeleri sıfırla
      const newAttempts = { ...loginAttempts };
      delete newAttempts[girisForm.kullaniciId];
      setLoginAttempts(newAttempts);
      
      // Custom claims'i al (eğer varsa)
      const user = auth.currentUser;
      let tokenClaims = null;
      if (user) {
        log('✅ Firebase Auth kullanıcı bilgileri:', {
          email: user.email,
          uid: user.uid,
          displayName: user.displayName
        });
        try {
          const tokenResult = await user.getIdTokenResult(true); // Force refresh
          tokenClaims = tokenResult.claims;
          log('✅ Custom claims:', tokenClaims);
          log('🔍 ROL KONTROLÜ:', {
            tokenRol: tokenClaims.rol,
            kullaniciRol: kullanici.rol,
            kullaniciId: tokenClaims.kullaniciId,
            subeId: tokenClaims.subeId
          });
          
          // V5.8: Eğer custom claims yoksa, HTTP Callable Function ile set etmeyi dene
          if (!tokenClaims.rol) {
            warn('⚠️ Custom claims yok! HTTP Callable Function ile set edilmeye çalışılıyor...');
            try {
              const { setCustomClaims } = await import('./utils/firebase');
              await setCustomClaims({
                kullaniciAd: kullanici.ad,
                rol: kullanici.rol,
                subeId: kullanici.subeId || null,
                kullaniciId: kullanici.id.toString()
              });
              log('✅ Custom claims set edildi (ilk giriş)');
              // Token'ı tekrar refresh et
              await user.getIdToken(true);
              const newTokenResult = await user.getIdTokenResult(true);
              tokenClaims = newTokenResult.claims;
              log('✅ Yeni custom claims:', tokenClaims);
            } catch (claimsError) {
              error('❌ Custom claims set etme hatası (ilk giriş):', claimsError);
              warn('⚠️ Custom claims yok! Script çalıştırılmalı: node set-custom-claims.js');
            }
          }
        } catch (error) {
          log('Custom claims alınamadı (normal, henüz eklenmemiş olabilir):', error);
        }
      } else {
        warn('⚠️ auth.currentUser null!');
      }
      
      // V5.7: Eski session'ları temizle (asenkron - girişi geciktirmesin)
      // Yeni session oluşturulurken eski session'lar arka planda silinecek
      // Bu şekilde giriş hızlı olur, çoklu cihaz kontrolü listener ile yapılacak
      (async () => {
        try {
          if (user) {
            await user.getIdToken(true); // Force refresh
          }
          const eskiSessions = await getDocs(query(collection(db, 'aktifSessions'), where('userId', '==', kullanici.id.toString())));
          if (eskiSessions.docs.length > 0) {
            // V5.10.19: Session temizleme log'u kaldırıldı (console spam önleme)
            const silmePromises = eskiSessions.docs.map(doc => deleteDoc(doc.ref));
            await Promise.all(silmePromises);
          }
        } catch (error) {
          // Session temizleme hatası kritik değil, sessizce devam et
          if (error.code !== 'permission-denied') {
            warn('⚠️ Eski session temizleme hatası:', error.message);
          }
        }
      })();

      // Session'ı ÖNCE oluştur (çoklu cihaz kontrolü için gerekli)
      let sessionId = null;
      try {
        const sessionRef = await addDoc(collection(db, 'aktifSessions'), {
          userId: kullanici.id.toString(),
          kullaniciAd: kullanici.ad,
          olusturmaTarihi: new Date()
        });
        sessionId = sessionRef.id;
        setMevcutSessionId(sessionId);
        // V5.10.20: Session oluşturuldu log'u kaldırıldı
      } catch (error) {
        warn('⚠️ Session oluşturma hatası:', error);
        setMevcutSessionId(null);
      }

      // V5.7: Token refresh sonrası listener'ların fresh token ile çalışması için kısa bir bekleme
      // (Firestore listener'lar zaten çalışıyor, sadece token'ın fresh olduğundan emin ol)
      // Giriş hızını artırmak için bekleme süresini kaldırdık
      
      // Aktif kullanıcıyı set et (session oluşturulduktan sonra)
      // V5.3: subeId doğrulama - Dergah Hayrat için subeId mutlaka kendi şube ID'si olmalı
      let finalSubeId = tokenClaims?.subeId || kullanici.subeId;
      
      // Dergah Hayrat için subeId doğrulama
      if (kullanici.rol === 'Şube Hayrat Sorumlusu') {
        // Dergah Hayrat'ın subeId'si null veya 'merkez' olamaz
        if (!finalSubeId || finalSubeId === 'merkez') {
          error('❌ Dergah Hayrat için subeId yanlış:', finalSubeId);
          warn('⚠️ Firestore veya Custom Claims\'de subeId kontrol edilmeli!');
          // Hata ver ama devam et (kullanıcı giriş yapabilsin, sonra düzeltilebilir)
        }
      }
      
      const girisYapanKullanici = {
        ...kullanici,
        rol: tokenClaims?.rol || kullanici.rol,
        subeId: finalSubeId,
        alanlar: kullanici.alanlar || [] // V5.9: Alanları da ekle
      };
      setAktifKullanici(girisYapanKullanici);
      try {
        localStorage.setItem('kumbaraAktifKullanici', JSON.stringify(girisYapanKullanici));
      } catch (storageError) {
        warn('⚠️ localStorage\'a kullanıcı kaydedilemedi:', storageError);
      }
      setAktifSayfa('anasayfa');
      setGirisForm({ kullaniciId: '', sifre: '' });
      
      // V4.7: Giriş sonrası listener'ların veriyi yüklediğinden emin olmak için log
      log('✅ Giriş tamamlandı, listener\'lar veriyi yükleyecek...');

      // V4.4: Token refresh'i arka planda yap (girişi yavaşlatmasın ama custom claims hazır olsun)
      if (user) {
        (async () => {
          try {
            await user.getIdToken(true); // Force refresh - custom claims yüklensin
            log('✅ Token refresh tamamlandı, custom claims hazır');
          } catch (tokenError) {
            warn('⚠️ Token refresh hatası (kritik değil):', tokenError);
          }
        })();
      }
      
    } catch (error) {
      // V5.5: Firestore fallback kaldırıldı - Sadece Firebase Auth hataları gösterilir
      error('❌ Giriş hatası:', error);
      alertToToast(toast, `❌ Giriş başarısız: ${error.message || 'Bilinmeyen hata'}`);
      return;
    }
  };

  // Menü tanımı
  const menuler = aktifKullanici ? (aktifKullanici.rol === 'Merkez Başkanı'
    ? [
        { id: 'anasayfa', label: '🏠 Anasayfa' },
        { id: 'subeler', label: '🏢 Şube Yönetimi' },
        { id: 'liste', label: '📋 Kumbara Listesi' },
        { id: 'teslim', label: '📦 Teslim Kumbaralar' },
        { id: 'acilan', label: '📚 Açılan Kumbaralar' },
        { id: 'raporlar', label: '📊 Raporlar' },
        { id: 'verigirisi', label: '📥 Veri Girişi' },
        { id: 'ayarlar', label: '⚙️ Ayarlar' }
      ]
    : aktifKullanici.rol === 'Kumbara Koordinatörü'
    ? [
          { id: 'anasayfa', label: '🏠 Anasayfa' },
          { id: 'yeni', label: '➕ Yeni Kumbara' },
          { id: 'liste', label: '📋 Kumbara Listesi' },
          { id: 'teslim', label: '📦 Teslim Kumbaralar' },
          { id: 'acilan', label: '📚 Açılan Kumbaralar' },
          { id: 'raporlar', label: '📊 Raporlar' },
          { id: 'verigirisi', label: '📥 Veri Girişi' }
      ]
    : aktifKullanici.rol === 'İl Merkez Hayrat Koordinatörü'
    ? [
        { id: 'anasayfa', label: '🏠 Anasayfa' },
        { id: 'subeler', label: '🏢 Şube Ekranları' },
        { id: 'mali', label: '💰 Mali İşlemler' },
        { id: 'raporlar', label: '📊 Raporlar' }
      ]
   : aktifKullanici.rol === 'Şube Hayrat Sorumlusu'
? [
    { id: 'anasayfa', label: '🏠 Anasayfa' },
    { id: 'mali', label: '💰 Mali İşlemler' },
    { id: 'raporlar', label: '📊 Raporlar' }
  ]
    : (aktifKullanici.rol === 'Saha Görevlisi' || aktifKullanici.rol === 'Kumbara Sorumlusu') ? [
        { id: 'anasayfa', label: '🏠 Anasayfa' },
        { id: 'yeni', label: '➕ Yeni Kumbara' }
      ] : [
        { id: 'anasayfa', label: '🏠 Anasayfa' },
        { id: 'yeni', label: '➕ Yeni Kumbara' }
      ]) : [];

  // Çıkış işlemi
  const cikisYap = async () => {
    // V4.7: Önce manuel çıkış flag'ini set et (onAuthStateChanged için)
    // Sonra state'i temizle, session'ı sil ve Firebase Auth'dan çıkış yap
    const sessionIdToDelete = mevcutSessionId;
    manuelCikisYapildi.current = true;
    setAktifKullanici(null);
    setMevcutSessionId(null);
    try {
      localStorage.removeItem('kumbaraAktifKullanici');
    } catch (storageError) {
      warn('⚠️ localStorage temizlenemedi:', storageError);
    }

    if (sessionIdToDelete) {
      try {
        await deleteDoc(doc(db, 'aktifSessions', sessionIdToDelete));
      } catch (error) {
        if (error.code !== 'permission-denied') {
          error('Session silme hatası:', error);
        }
      }
    }

    try {
      await signOut(auth);
      log('✅ Firebase Auth çıkış yapıldı');
    } catch (error) {
      error('Firebase Auth çıkış hatası:', error);
    }
  };

  const rolAdi = aktifKullanici && (
    aktifKullanici.rol === 'Merkez Başkanı' ? 'İl Merkez Başkanı'
    : aktifKullanici.rol === 'İl Merkez Hayrat Koordinatörü' ? 'İl Hayrat Sorumlusu'
    : aktifKullanici.rol === 'Kumbara Koordinatörü' ? 'İl Kumbara Koordinatörü'
    : aktifKullanici.rol === 'Şube Hayrat Sorumlusu' ? `${subeler.find(s => s.id === aktifKullanici.subeId)?.ad || 'Dergah'} Hayrat Sorumlusu`
    : aktifKullanici.rol === 'Saha Görevlisi' ? (aktifKullanici.subeId ? `${subeler.find(s => s.id === aktifKullanici.subeId)?.ad || 'Dergah'} Kumbara Sorumlusu` : 'İl Kumbara Sorumlusu')
    : 'Buhara Samsun Vakfı'
  );

  if (!aktifKullanici) {
    return (
      <div style={styles.loginCard}>
        <div style={styles.card}>
          <div style={{textAlign: 'center', marginBottom: '20px'}}>
           <img src="/teklogo.svg" alt="Logo" style={{width: '100px', height: '100px', borderRadius: '50%', marginBottom: '20px'}} />
          </div>
          <h1 style={styles.title}>Buhara Samsun Vakfı</h1>
          <p style={styles.subtitle}>Giriş Bilgilerinizi Girin</p>
          <select value={girisForm.kullaniciId} onChange={(e) => setGirisForm({...girisForm, kullaniciId: e.target.value})} style={styles.input}>
            <option value="">Kullanıcı Seçin</option>
            {kullanicilar.length === 0 ? (
              <option value="" disabled>Kullanıcılar yükleniyor...</option>
            ) : (
              kullanicilar.map(k => {
                const rolBasligi = k.rol === 'Merkez Başkanı' ? 'İl Merkez Başkanı'
                  : k.rol === 'İl Merkez Hayrat Koordinatörü' ? 'İl Hayrat Sorumlusu'
                  : k.rol === 'Kumbara Koordinatörü' ? 'İl Kumbara Koordinatörü'
                  : k.rol === 'Şube Hayrat Sorumlusu' ? 'Dergah Hayrat Sorumlusu'
                  : k.rol === 'Saha Görevlisi' ? 'Kumbara Sorumlusu'
                  : k.rol;
                // V5.9: Hem id hem firestoreId kullan (yeni kullanıcılar için)
                const kullaniciId = k.id?.toString() || k.firestoreId;
                return <option key={k.firestoreId || k.id} value={kullaniciId}>{k.ad} ({rolBasligi})</option>;
              })
            )}
          </select>
          <div style={{position: 'relative', width: '100%'}}>
            <input
              type={sifreGoster ? 'text' : 'password'}
              placeholder="Şifre"
              value={girisForm.sifre}
              onChange={(e) => setGirisForm({...girisForm, sifre: e.target.value})}
              onKeyPress={(e) => e.key === 'Enter' && girisYap()}
              style={{...styles.input, paddingRight: '40px'}}
            />
            <span
              onClick={() => setSifreGoster(!sifreGoster)}
              style={{position: 'absolute', right: '12px', top: 'calc(50% - 14px)', cursor: 'pointer', display: 'flex', color: '#888'}}
            >
              {sifreGoster ? <EyeOff size={20} /> : <Eye size={20} />}
            </span>
          </div>
          <button onClick={girisYap} style={styles.button}>🔐 Giriş Yap</button>
          {!isInstalled && (
            <button 
              onClick={handleInstallClick} 
              style={{
                ...styles.button,
                marginTop: '15px',
                background: 'linear-gradient(135deg, #A26F29 0%, #284D3A 100%)',
                fontSize: '14px',
                padding: '10px 20px'
              }}
            >
              📱 Ana Ekrana Ekle
            </button>
          )}
        </div>
      </div>
    );
  }

// V4.1.4: VeriGirisi component artık components/VeriGirisi.js'de

  return (
    <div style={styles.container}>
      <style>{`
        .app-hamburger-btn { display: none; }
        .app-desktop-nav { display: flex; }
        .app-header-desktop { display: flex; }
        .app-header-mobile { display: none; }
        @media (max-width: 768px) {
          .app-hamburger-btn { display: flex !important; }
          .app-desktop-nav { display: none !important; }
          .app-header-desktop { display: none !important; }
          .app-header-mobile { display: block !important; }
        }
      `}</style>

      <div style={styles.header}>
        {/* Masaüstü Header */}
        <div className="app-header-desktop" style={styles.headerContent}>
          <div>
            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '40px'}}>
              <img src="/logo.svg" alt="Logo" style={{width: '175px', height: '50px'}} />
              <h1 style={{margin: 0, fontSize: '24px', fontWeight: 'bold'}}>{rolAdi}</h1>
            </div>
            <p style={{margin: '5px 0 0 0', color: '#718096', fontSize: '14px'}}>{aktifKullanici.ad}{aktifKullanici.alanlar && aktifKullanici.alanlar.length > 0 ? ` - ${aktifKullanici.alanlar.join(', ')}` : aktifKullanici.bolge && ` - ${aktifKullanici.bolge}`}</p>
          </div>
          <button onClick={cikisYap} style={styles.btnDanger}>🚪 Çıkış</button>
        </div>

        {/* Mobil Header */}
        <div className="app-header-mobile">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <img src="/logo.svg" alt="Logo" style={{width: '140px', height: '40px'}} />
            <button
              className="app-hamburger-btn"
              onClick={() => setMobilMenuAcik(true)}
              aria-label="Menüyü aç"
              style={{
                alignItems: 'center',
                justifyContent: 'center',
                width: '44px',
                height: '44px',
                background: '#284D3A',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '22px',
                cursor: 'pointer',
                flexShrink: 0
              }}
            >☰</button>
          </div>
          <h1 style={{margin: '12px 0 0 0', fontSize: '18px', fontWeight: 'bold'}}>{rolAdi}</h1>
          <p style={{margin: '3px 0 0 0', color: '#718096', fontSize: '13px'}}>{aktifKullanici.ad}{aktifKullanici.alanlar && aktifKullanici.alanlar.length > 0 ? ` - ${aktifKullanici.alanlar.join(', ')}` : aktifKullanici.bolge && ` - ${aktifKullanici.bolge}`}</p>
          <button
            onClick={cikisYap}
            style={{
              display: 'inline-block',
              marginTop: '8px',
              padding: 0,
              background: 'none',
              border: 'none',
              color: '#A26F29',
              fontSize: '15.6px',
              fontWeight: 'bold',
              textDecoration: 'underline',
              cursor: 'pointer'
            }}
          >🚪 Çıkış</button>
        </div>

        <div className="app-desktop-nav" style={{...styles.headerContent, marginTop: 0}}>
          <div style={styles.nav}>
            {menuler.map(m => (
              <button
                key={m.id}
                onClick={() => { setAktifSayfa(m.id); if (m.id !== 'mali') setMerkezMaliSube(null); }}
                style={{
                  ...styles.navBtn,
                  background: aktifSayfa === m.id ? 'linear-gradient(135deg, #284D3A 0%, #A26F29 100%)' : '#f7fafc',
                  color: aktifSayfa === m.id ? 'white' : '#4a5568'
                }}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Mobil Hamburger Menü - Drawer */}
      {mobilMenuAcik && (
        <div
          onClick={() => setMobilMenuAcik(false)}
          style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end'}}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{width: '80%', maxWidth: '320px', height: '100%', background: 'white', boxShadow: '-2px 0 12px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', padding: '20px', gap: '8px', overflowY: 'auto', boxSizing: 'border-box'}}
          >
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
              <span style={{fontWeight: 'bold', fontSize: '18px', color: '#284D3A'}}>📋 Menü</span>
              <button
                onClick={() => setMobilMenuAcik(false)}
                aria-label="Menüyü kapat"
                style={{background: 'none', border: 'none', fontSize: '26px', cursor: 'pointer', color: '#4a5568', lineHeight: 1}}
              >✕</button>
            </div>
            {menuler.map(m => (
              <button
                key={m.id}
                onClick={() => {
                  setAktifSayfa(m.id);
                  if (m.id !== 'mali') setMerkezMaliSube(null);
                  setMobilMenuAcik(false);
                }}
                style={{
                  ...styles.navBtn,
                  width: '100%',
                  textAlign: 'left',
                  background: aktifSayfa === m.id ? 'linear-gradient(135deg, #284D3A 0%, #A26F29 100%)' : '#f7fafc',
                  color: aktifSayfa === m.id ? 'white' : '#4a5568'
                }}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={styles.content}>
        {yukleniyor ? (
          <LoadingSpinner size={50} />
        ) : (
        <Suspense fallback={<LoadingSpinner />}>
          {aktifKullanici.rol === 'Merkez Başkanı' ? (
            <>
              {aktifSayfa === 'anasayfa' && <MerkezBaskanAnasayfa kumbaralar={kumbaralar} teslimEdilenler={teslimEdilenler} acilmisKumbaralar={acilmisKumbaralar} dagitimdaBekleyen={dagitimdaBekleyen} subeler={subeler} aktifKullanici={aktifKullanici} />}
              {aktifSayfa === 'saha' && <SahaAnasayfa aktifKullanici={aktifKullanici} kumbaralar={kumbaralar} setKumbaralar={setKumbaralar} sahaKonum={sahaKonum} teslimEdilenler={teslimEdilenler} setTeslimEdilenler={setTeslimEdilenler} acilmisKumbaralar={acilmisKumbaralar} />}
              {aktifSayfa === 'subeler' && <SubeYonetimi subeler={subeler} aktifKullanici={aktifKullanici} onMaliIslemler={(sube) => { setMerkezMaliSube(sube || {id: 'merkez', ad: 'İl Hayrat Kasası'}); setAktifSayfa('mali'); }} />}
              {aktifSayfa === 'liste' && <KumbaraListesi kumbaralar={kumbaralar} aktifKullanici={aktifKullanici} setKumbaralar={setKumbaralar} acilmisKumbaralar={acilmisKumbaralar} />}
              {aktifSayfa === 'teslim' && <TeslimKumbaralar teslimEdilenler={teslimEdilenler} setTeslimEdilenler={setTeslimEdilenler} acilmisKumbaralar={acilmisKumbaralar} setAcilmisKumbaralar={setAcilmisKumbaralar} aktifKullanici={aktifKullanici} />}
              {aktifSayfa === 'acilan' && <AcilanKumbaralar acilmisKumbaralar={acilmisKumbaralar} setAcilmisKumbaralar={setAcilmisKumbaralar} aktifKullanici={aktifKullanici} subeler={subeler} />}
              {aktifSayfa === 'verigirisi' && <VeriGirisi kumbaralar={kumbaralar} setKumbaralar={setKumbaralar} kullanicilar={kullanicilar} acilmisKumbaralar={acilmisKumbaralar} />}
              {aktifSayfa === 'raporlar' && <Raporlar kumbaralar={kumbaralar} acilmisKumbaralar={acilmisKumbaralar} aktifKullanici={aktifKullanici} subeler={subeler} />}
              {aktifSayfa === 'ayarlar' && <Ayarlar subeler={subeler} aktifKullanici={aktifKullanici} kumbaralar={kumbaralar} setKumbaralar={setKumbaralar} acilmisKumbaralar={acilmisKumbaralar} />}
              {aktifSayfa === 'mali' && !merkezMaliSube && (
                <div style={{padding: '30px', background: 'white', borderRadius: '15px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)'}}>
                  <h2 style={{fontSize: '22px', fontWeight: 'bold', marginBottom: '8px'}}>💰 Mali İşlemler</h2>
                  <p style={{color: '#718096', marginBottom: '25px'}}>İşlem yapmak istediğiniz dergahı seçin:</p>
                  <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '15px'}}>
                    {subeler.filter(s => s.id !== 'merkez').map(sube => (
                      <button
                        key={sube.id}
                        onClick={() => setMerkezMaliSube(sube)}
                        style={{
                          padding: '20px',
                          background: 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)',
                          border: '2px solid #e2e8f0',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          textAlign: 'left',
                          fontSize: '15px',
                          fontWeight: 'bold',
                          color: '#2d3748',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#284D3A'; e.currentTarget.style.background = 'linear-gradient(135deg, #e6ffed 0%, #c6f6d5 100%)'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)'; }}
                      >
                        🏢 {sube.ad}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {aktifSayfa === 'mali' && merkezMaliSube && (
                <>
                  <div style={{marginBottom: '15px'}}>
                    <button
                      onClick={() => setMerkezMaliSube(null)}
                      style={{padding: '8px 18px', background: '#e2e8f0', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', color: '#4a5568', fontSize: '14px'}}
                    >
                      ← Dergah Seç
                    </button>
                  </div>
                  <MaliIslemler aktifKullanici={{...aktifKullanici, subeId: merkezMaliSube.id}} subeler={subeler} />
                </>
              )}
            </>
          ) : aktifKullanici.rol === 'İl Merkez Hayrat Koordinatörü' ? (
            <>
              {aktifSayfa === 'anasayfa' && <IlHayratAnasayfa kumbaralar={kumbaralar} teslimEdilenler={teslimEdilenler} acilmisKumbaralar={acilmisKumbaralar} subeler={subeler} aktifKullanici={aktifKullanici} />}
              {aktifSayfa === 'subeler' && <SubeYonetimi subeler={subeler} aktifKullanici={aktifKullanici} onMaliIslemler={(sube) => { setMerkezMaliSube(sube || {id: 'merkez', ad: 'İl Hayrat Kasası'}); setAktifSayfa('mali'); }} />}
              {aktifSayfa === 'raporlar' && <Raporlar kumbaralar={kumbaralar} acilmisKumbaralar={acilmisKumbaralar} aktifKullanici={aktifKullanici} subeler={subeler} />}
              {aktifSayfa === 'mali' && <MaliIslemler aktifKullanici={aktifKullanici} subeler={subeler} />}
            </>
          ) : (aktifKullanici.rol === 'Kumbara Koordinatörü' || aktifKullanici.rol === 'İl Kumbara Koordinatörü') ? (
            <>
              {aktifSayfa === 'anasayfa' && (() => {
                error('🚨🚨🚨 APP.JS - YONETICIANASAYFA RENDER EDILIYOR - KESIN LOG:', {
                  aktifSayfa,
                  rol: aktifKullanici.rol,
                  kumbaralarLength: kumbaralar?.length || 0,
                  acilmisKumbaralarLength: acilmisKumbaralar?.length || 0,
                  timestamp: new Date().toISOString()
                });
                return <YoneticiAnasayfa kumbaralar={kumbaralar} teslimEdilenler={teslimEdilenler} acilmisKumbaralar={acilmisKumbaralar} dagitimdaBekleyen={dagitimdaBekleyen} sahaKonum={sahaKonum} setKumbaralar={setKumbaralar} setTeslimEdilenler={setTeslimEdilenler} />;
              })()}
              {aktifSayfa === 'saha' && <SahaAnasayfa aktifKullanici={aktifKullanici} kumbaralar={kumbaralar} setKumbaralar={setKumbaralar} sahaKonum={sahaKonum} teslimEdilenler={teslimEdilenler} setTeslimEdilenler={setTeslimEdilenler} acilmisKumbaralar={acilmisKumbaralar} />}
              {aktifSayfa === 'yeni' && <YeniKumbara aktifKullanici={aktifKullanici} kumbaralar={kumbaralar} setKumbaralar={setKumbaralar} />}
              {aktifSayfa === 'liste' && <KumbaraListesi kumbaralar={kumbaralar} aktifKullanici={aktifKullanici} setKumbaralar={setKumbaralar} acilmisKumbaralar={acilmisKumbaralar} />}
              {aktifSayfa === 'teslim' && <TeslimKumbaralar teslimEdilenler={teslimEdilenler} setTeslimEdilenler={setTeslimEdilenler} acilmisKumbaralar={acilmisKumbaralar} setAcilmisKumbaralar={setAcilmisKumbaralar} aktifKullanici={aktifKullanici} />}
              {aktifSayfa === 'acilan' && <AcilanKumbaralar acilmisKumbaralar={acilmisKumbaralar} setAcilmisKumbaralar={setAcilmisKumbaralar} aktifKullanici={aktifKullanici} subeler={subeler} />}
              {aktifSayfa === 'verigirisi' && <VeriGirisi kumbaralar={kumbaralar} setKumbaralar={setKumbaralar} kullanicilar={kullanicilar} acilmisKumbaralar={acilmisKumbaralar} />}
              {aktifSayfa === 'raporlar' && <Raporlar kumbaralar={kumbaralar} acilmisKumbaralar={acilmisKumbaralar} aktifKullanici={aktifKullanici} subeler={subeler} />}
            </>
          ) : aktifKullanici.rol === 'Şube Hayrat Sorumlusu' ? (
            <>
              {aktifSayfa === 'anasayfa' && <SubeHayratAnasayfa aktifKullanici={aktifKullanici} subeler={subeler} />}
              {aktifSayfa === 'mali' && <MaliIslemler aktifKullanici={aktifKullanici} subeler={subeler} />}
              {aktifSayfa === 'raporlar' && <Raporlar kumbaralar={kumbaralar} acilmisKumbaralar={acilmisKumbaralar} aktifKullanici={aktifKullanici} subeler={subeler} />}
            </>
          ) : (aktifKullanici.rol === 'Saha Görevlisi' || aktifKullanici.rol === 'Kumbara Sorumlusu') ? (
            <>
              {aktifSayfa === 'anasayfa' && <SahaAnasayfa aktifKullanici={aktifKullanici} kumbaralar={kumbaralar} setKumbaralar={setKumbaralar} sahaKonum={sahaKonum} teslimEdilenler={teslimEdilenler} setTeslimEdilenler={setTeslimEdilenler} acilmisKumbaralar={acilmisKumbaralar} />}
              {aktifSayfa === 'yeni' && <YeniKumbara aktifKullanici={aktifKullanici} kumbaralar={kumbaralar} setKumbaralar={setKumbaralar} />}
            </>
          ) : (
            <>
              {aktifSayfa === 'anasayfa' && <SahaAnasayfa aktifKullanici={aktifKullanici} kumbaralar={kumbaralar} setKumbaralar={setKumbaralar} sahaKonum={sahaKonum} teslimEdilenler={teslimEdilenler} setTeslimEdilenler={setTeslimEdilenler} acilmisKumbaralar={acilmisKumbaralar} />}
              {aktifSayfa === 'yeni' && <YeniKumbara aktifKullanici={aktifKullanici} kumbaralar={kumbaralar} setKumbaralar={setKumbaralar} />}
            </>
          )}
        </Suspense>
        )}
      </div>
    </div>
  );
}

// V4.1.4: MerkezBaskanAnasayfa, IlHayratAnasayfa, SubeHayratAnasayfa, SubeYonetimi, SubeDetay components artık components/ klasöründe

// V4.1.4: YoneticiAnasayfa, KumbaraListesi, TeslimKumbaralar, AcilanKumbaralar components artık components/ klasöründe

// V4.1.4: KumbaraListesi component artık components/KumbaraListesi.js'de

// V4.1.4: TeslimKumbaralar component artık components/TeslimKumbaralar.js'de

// V4.1.4: AcilanKumbaralar component artık components/AcilanKumbaralar.js'de
// V4.1.4: Raporlar component artık components/Raporlar.js'de

// V4.1.4: SahaAnasayfa component artık components/SahaAnasayfa.js'de
// V4.1.4: Ayarlar component artık components/Ayarlar.js'de

// V4.1.4: SahaAnasayfa component artık components/SahaAnasayfa.js'de

// V4.1.4: KumbaraItem component artık components/KumbaraItem.js'de
// V4.1.4: Ayarlar component artık components/Ayarlar.js'de

// V5.9: OFFLINE DESTEĞİ - Service Worker kaydı useEffect içinde yapılıyor, burada sadece fallback

export default App;